import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { CommonSelect } from '@/components/common/CommonSelect';
import FormField from '@/components/common/FormField';
import Button from '@/components/common/Button';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { showToastNotification } from '@/utility/helper';
import { errorTypes } from '@/utility/validation';
import { IStore, IStoreResponse } from '@/interfaces/Store/store.interface';
import { SectionCard } from '@/components/common/SectionCard';
import { Landmark } from 'lucide-react';
import { useAppDispatch } from '@/redux/store';
import { fetchUserDetails } from '@/redux/features/UserSlice';

interface IBankDetailsForm {
    bankName: string;
    accountNumber: string;
    accountType: string;
    branchName: string;
    branchCode: string;
    accountHolderName: string;
}

const StoreBankDetails = () => {
    const [fetching, setFetching] = useState(true);
    const [store, setStore] = useState<IStore | null>(null);
    const dispatch = useAppDispatch();

    const {
        register,
        handleSubmit,
        reset,
        control,
        formState: { errors, isSubmitting },
    } = useForm<IBankDetailsForm>();

    useEffect(() => {
        const fetchStoreData = async () => {
            try {
                setFetching(true);
                const response = await callApi<{ data: IStore }>(
                    `${API_URL}${API_ENDPOINT.stores}/profile`,
                    { method: 'GET' }
                );

                if (response?.data) {
                    const storeData = response.data;
                    setStore(storeData);

                    reset({
                        bankName: storeData.user?.wallet?.bankName || '',
                        accountNumber: storeData.user?.wallet?.accountNumber || '',
                        accountType: storeData.user?.wallet?.accountType || '',
                        branchName: storeData.user?.wallet?.branchName || '',
                        branchCode: storeData.user?.wallet?.branchCode || '',
                        accountHolderName: storeData.user?.wallet?.accountHolderName || '',
                    });
                }
            } catch (error) {
                ThrowError(error);
            } finally {
                setFetching(false);
            }
        };

        fetchStoreData();
    }, [reset]);

    const onSubmit = async (data: IBankDetailsForm) => {
        if (!store) return;
        try {
            const payload = {
                accountHolderName: data.accountHolderName?.trim(),
                bankName: data.bankName?.trim(),
                branchName: data.branchName?.trim(),
                branchCode: data.branchCode?.trim(),
                accountType: data.accountType?.trim(),
                accountNumber: data.accountNumber?.trim()
            };

            const response = await callApi<IStoreResponse>(
                `${API_URL}${API_ENDPOINT.wallet}`,
                {
                    method: 'POST',
                    data: payload
                }
            );
            dispatch(fetchUserDetails());
            showToastNotification('success', response?.message || 'Bank details updated successfully');
        } catch (error) {
            ThrowError(error);
        }
    };

    if (fetching) {
        return (
            <div className="flex justify-center items-center py-12 bg-card border rounded-xl">
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <SectionCard title="Banking Details" icon={<Landmark className="w-5 h-5 text-muted-foreground" />}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                        label="Bank Name"
                        name="bankName"
                        type="text"
                        requiredIcon
                        placeholder="e.g. Standard Bank"
                        error={errors.bankName?.message}
                        {...register('bankName', { ...errorTypes.required("Bank Name") })}
                    />

                    <FormField
                        label="Account Holder Name"
                        name="accountHolderName"
                        type="text"
                        requiredIcon
                        placeholder="e.g. John Doe"
                        error={errors.accountHolderName?.message}
                        {...register('accountHolderName', { ...errorTypes.required("Account Holder Name") })}
                    />

                    <FormField
                        label="Account Number"
                        name="accountNumber"
                        type="number"
                        requiredIcon
                        placeholder="e.g. 123456789"
                        error={errors.accountNumber?.message}
                        onWheel={(e) => e.currentTarget.blur()}
                        {...register('accountNumber', { ...errorTypes.required("Account Number") })}
                    />


                    <Controller
                        name="accountType"
                        control={control}
                        rules={{ ...errorTypes.required("Account Type") }}
                        render={({ field }) => (
                            <FormField
                                label="Account Type"
                                name="accountType"
                                type='select'
                                requiredIcon
                                options={[
                                    { label: 'Savings', value: 'saving' },
                                    { label: 'Current', value: 'current' }
                                ]}
                                error={errors.accountType?.message}
                                value={field.value}
                                onValueChange={field.onChange}
                            />
                        )}
                    />
                    <FormField
                        label="Branch Name"
                        name="branchName"
                        type="text"
                        placeholder="e.g. Main Branch"
                        requiredIcon
                        error={errors.branchName?.message}
                        {...register('branchName', { ...errorTypes.required("Branch Name") })}
                    />

                    <FormField
                        label="Branch Code"
                        name="branchCode"
                        type="text"
                        placeholder="e.g. 051001"
                        requiredIcon
                        error={errors.branchCode?.message}
                        {...register('branchCode', { ...errorTypes.required("Branch Code") })}
                    />
                </div>
            </SectionCard>

            <div className="flex justify-end pt-4">
                <Button type="submit" isLoading={isSubmitting} loadingText="Saving Details..." className="px-10 h-11 text-base">
                    Save Bank Details
                </Button>
            </div>
        </form>
    );
};

export default StoreBankDetails;
